import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import AssistantPhotoRoundedIcon from "@mui/icons-material/AssistantPhotoRounded";
import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import AppLayout from "../../components/ui/AppLayout";
import PageHeader from "../../components/ui/PageHeader";
import SectionCard from "../../components/ui/SectionCard";
import LoadingState from "../../components/ui/LoadingState";
import PlayerAvatar from "../../components/ui/PlayerAvatar";
import TeamCrest from "../../components/ui/TeamCrest";
import { cardColor } from "./GameCard";
import api from "../../api";
import tokens from "../../theme/tokens";

const CACHE_KEY_JOGADORES = "jogadoresCache";
const CACHE_EXPIRATION = 1 * 24 * 60 * 60 * 1000; // 1 dia em milissegundos

// Lançamento de gol, assistência ou cartão para um jogador de um jogo.
const EditarJogoDetalhes = () => {
  const { jogoId } = useParams();
  const navigate = useNavigate();
  const [jogo, setJogo] = useState(null);
  const [times, setTimes] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedAction, setSelectedAction] = useState("gol"); // 'gol', 'assistencia', 'cartao'
  const [selectedCard, setSelectedCard] = useState(null); // 'amarelo', 'azul', 'vermelho'
  const [snackbar, setSnackbar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jogoResponse = await api.get(`/jogos/${jogoId}`);
        const jogoData = jogoResponse.data.jogo;
        setJogo(jogoData);

        const cachedData = localStorage.getItem(CACHE_KEY_JOGADORES);
        const cachedTimestamp = localStorage.getItem(`${CACHE_KEY_JOGADORES}_timestamp`);
        let timesData;
        if (cachedData && cachedTimestamp && new Date().getTime() - parseInt(cachedTimestamp, 10) <= CACHE_EXPIRATION) {
          timesData = JSON.parse(cachedData);
        }
        if (timesData === undefined) {
          const response = await api.post("/report/getTimes", { torneioId: jogoData.torneio });
          timesData = response.data;
        }

        const timesRelacionados = {};
        Object.keys(timesData).forEach((teamName) => {
          if (teamName === jogoData.timeA.nome || teamName === jogoData.timeB.nome) {
            timesRelacionados[teamName] = timesData[teamName];
          }
        });
        setTimes(timesRelacionados);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      }
    };

    fetchData();
  }, [jogoId]);

  const handleSave = async () => {
    if (!selectedPlayer || (selectedAction === "cartao" && !selectedCard)) {
      setSnackbar({ severity: "error", message: "Selecione o jogador e, para cartões, a cor." });
      return;
    }

    let dataToSave = { jogoId };
    if (selectedAction === "cartao") {
      dataToSave.cartoes = { jogador: selectedPlayer._id, tipo: selectedCard };
    } else if (selectedAction === "gol") {
      dataToSave.gols = { jogador: selectedPlayer._id, quantidade: 1 };
    } else if (selectedAction === "assistencia") {
      dataToSave.assistencias = { jogador: selectedPlayer._id, quantidade: 1 };
    }

    setSaving(true);
    try {
      await api.put("/jogos", dataToSave);
      navigate("/listar-jogos");
    } catch (error) {
      setSnackbar({ severity: "error", message: "Erro ao salvar informações." });
    }
    setSaving(false);
  };

  if (!jogo) return <AppLayout><LoadingState /></AppLayout>;

  return (
    <AppLayout>
      <PageHeader backTo="/listar-jogos" eyebrow="Lançar evento" title={`${jogo.timeA.nome} × ${jogo.timeB.nome}`} subtitle={`Placar ${jogo.placar.timeA} × ${jogo.placar.timeB}`} />

      <SectionCard title="1. Tipo de lançamento" sx={{ mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <ToggleButtonGroup exclusive color="primary" value={selectedAction} onChange={(e, value) => value && setSelectedAction(value)}>
            <ToggleButton value="gol"><SportsSoccerRoundedIcon sx={{ mr: 1 }} />Gol</ToggleButton>
            <ToggleButton value="assistencia"><AssistantPhotoRoundedIcon sx={{ mr: 1 }} />Assistência</ToggleButton>
            <ToggleButton value="cartao"><StyleRoundedIcon sx={{ mr: 1 }} />Cartão</ToggleButton>
          </ToggleButtonGroup>
          {selectedAction === "cartao" && (
            <Stack direction="row" spacing={1}>
              {["amarelo", "azul", "vermelho"].map((tipo) => (
                <Button
                  key={tipo}
                  variant={selectedCard === tipo ? "contained" : "outlined"}
                  onClick={() => setSelectedCard(tipo)}
                  sx={{ bgcolor: selectedCard === tipo ? cardColor[tipo] : undefined, borderColor: cardColor[tipo], color: selectedCard === tipo ? "#fff" : cardColor[tipo], "&:hover": { bgcolor: cardColor[tipo], color: "#fff" }, textTransform: "capitalize" }}
                >
                  {tipo}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="2. Jogador" subtitle="Selecione quem fez o lance" sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          {Object.entries(times).map(([teamName, team]) => (
            <Grid item xs={12} md={6} key={teamName}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TeamCrest name={teamName} size={28} />
                <Typography fontWeight={700}>{teamName}</Typography>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 1 }}>
                {team.jogadores.map((jogador) => {
                  const selected = selectedPlayer && selectedPlayer._id === jogador._id;
                  return (
                    <Card key={jogador._id} sx={{ borderColor: selected ? tokens.color.brand : undefined, borderWidth: selected ? 2 : 1, bgcolor: selected ? tokens.color.brandSoft : undefined }}>
                      <CardActionArea onClick={() => setSelectedPlayer(jogador)} sx={{ p: 1.5, textAlign: "center" }}>
                        <PlayerAvatar src={jogador.foto} name={jogador.nome} size={56} sx={{ mx: "auto", mb: 1 }} />
                        <Typography variant="body2" fontWeight={600} noWrap>{jogador.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">#{jogador.numeroCamisa}</Typography>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionCard>

      <Card sx={{ p: 2, position: "sticky", bottom: 16 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {selectedPlayer && <PlayerAvatar src={selectedPlayer.foto} name={selectedPlayer.nome} size={40} />}
            <Typography noWrap>
              {selectedPlayer ? <strong>{selectedPlayer.nome}</strong> : "Nenhum jogador selecionado"}
              {selectedPlayer && ` · ${selectedAction === "cartao" ? `cartão ${selectedCard || "?"}` : selectedAction}`}
            </Typography>
          </Stack>
          <Button variant="contained" size="large" onClick={handleSave} disabled={saving || !selectedPlayer}>Salvar</Button>
        </Stack>
      </Card>

      <Snackbar open={Boolean(snackbar)} autoHideDuration={6000} onClose={() => setSnackbar(null)}>
        {snackbar ? <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default EditarJogoDetalhes;
